/**
 * Breakpoints for the Tray Ticket Visualizer.
 *
 * The dialog used to have exactly one: `max-width: 1199.95px`, which swapped
 * the whole toolbar for a drawer in a single step. Between a laptop and a
 * phone there was nothing, so a 1000px window - an ordinary half-screen split
 * on a nurse station monitor - got the phone layout.
 *
 * Three stages now, and every value is one the app already uses (see
 * `src/theme/responsive.css`); no new breakpoints are invented here.
 *
 *   >= 1200   full ribbon on one row, ticket beside its messages
 *   900-1199  ribbon still inline but allowed to wrap; body stacks
 *   768-899   ribbon folds into the drawer
 *   < 768     drawer, plus the dialog goes edge-to-edge
 *
 * Kept as a pure function so the staging is testable without mounting a
 * dialog that needs six queries and a facility in the store.
 */

/** Widths, in px, at which the layout changes. Mirrors responsive.css. */
export const TTV_BREAKPOINTS = {
  /** Below this the dialog is edge-to-edge - no margin, no rounded corners. */
  fullScreen: 768,
  /** Below this the ribbon is replaced by the drawer. */
  drawer: 900,
  /** Below this the ribbon wraps and the ticket stacks above its messages. */
  narrow: 1200,
} as const;

/**
 * `useMediaQuery` strings for the widths above.
 *
 * `- 0.05` rather than `- 1`: a fractional viewport width (browser zoom, or a
 * device pixel ratio that isn't a whole number) lands between the two integers
 * and would otherwise satisfy neither the max- nor the min- query.
 */
export const TTV_MEDIA = {
  fullScreen: `(max-width:${TTV_BREAKPOINTS.fullScreen - 0.05}px)`,
  drawer: `(max-width:${TTV_BREAKPOINTS.drawer - 0.05}px)`,
  narrow: `(max-width:${TTV_BREAKPOINTS.narrow - 0.05}px)`,
} as const;

export type ToolbarMode = 'row' | 'wrap' | 'drawer';

export interface VisualizerLayout {
  /** Dialog fills the viewport: no margin, no radius. */
  fullScreen: boolean;
  /** How the ribbon presents itself at this width. */
  toolbar: ToolbarMode;
  /** Ticket and its special-messages column stack instead of sitting side by side. */
  stackBody: boolean;
}

export function resolveVisualizerLayout(width: number): VisualizerLayout {
  return {
    fullScreen: width < TTV_BREAKPOINTS.fullScreen,
    toolbar:
      width < TTV_BREAKPOINTS.drawer ? 'drawer'
        : width < TTV_BREAKPOINTS.narrow ? 'wrap'
          : 'row',
    stackBody: width < TTV_BREAKPOINTS.narrow,
  };
}
